import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DeliveryOption, PrintSize } from '../../prop-types';
import classNames from 'classnames';
import {
  sizesWithPrice,
  sizesWithRootImage,
} from '../../common/totalizing-mixin';
import Photo from '../photos-grid/photo';
import SimpleThumbnail from '../photos-grid/photo/simple-thumbnail';
import MultiSelectModal from '../multi-select/multi-select-modal';
import { useMultiSelect } from '../multi-select/use-multi-select';
import ResizeOptionsModal from '../resize-options-modal';
import PreviewBadges from './preview-badges';
import PhotoRemoveConfirmationModal from '../../common/photo-remove-confirmation-modal';
import Paginator from '../../../_utils/paginator';
import { usePagination } from '../../../_utils/paginator/use-pagination';
import Button from '../../../_common/button';
import Footer from '../footer';

const PHOTOS_PER_PAGE = 25;

export default function PhotoListView({
  handleSinglePhotoSelection,
  orderedPhotos,
  recommendedPrintSizes,
  sizes,
  availableSizes,
  onAdvancedEditPhoto,
  deliveryOption,
  showMultiSelectModal,
  handleShowMultiSelectModal,
  viewingSizeKey,
  invalidDeliveryOptionSelected,
  handleAddPhotos,
  hideSummaryDropdown,
}) {
  const [showResizeOptionsModal, setShowResizeModal] = useState(false);
  const [
    showPhotoRemoveConfirmation,
    setShowPhotoRemoveConfirmation,
  ] = useState(false);
  const multiselectPhotos = orderedPhotos.reduce(
    (memo, size) => memo.concat([...size.photos]),
    []
  );
  const {
    isMultiSelected,
    handleMultiSelectItem,
    handleMultiSelectAll,
    handleMultiUnSelectAll,
    handleMultiDelete,
    handleMultiResize,
    multiSelectItemsCount,
    maximumSelected,
  } = useMultiSelect({
    photos: multiselectPhotos,
    isActive: showMultiSelectModal,
  });

  const displayedPhotos = orderedPhotos.flatMap(
    ({ id, photos = [], isLastPhoto }) =>
      photos.map(p => ({ id, photo: p, isLastPhoto }))
  );
  const {
    page,
    paginatedItems,
    totalPageCount,
    handlePageNext,
    handlePagePrevious,
    handleGoToPage,
  } = usePagination({ items: displayedPhotos, itemsPerPage: PHOTOS_PER_PAGE });

  useEffect(() => {
    handleGoToPage(1);
  }, [viewingSizeKey]); // reset page if size filter changes

  const photoRemoveConfirmationText =
    (multiSelectItemsCount > 1 &&
      'Are you sure you want to remove these photos?') ||
    'Are you sure you want to remove this photo?';

  useEffect(() => {
    if (showMultiSelectModal && multiSelectItemsCount < 1) {
      handleShowMultiSelectModal();
    }
  }, [multiSelectItemsCount]);

  function handleResizeOptionsModal() {
    setShowResizeModal(prevResizeOptionsModal => !prevResizeOptionsModal);
  }

  function handlePhotoRemoveModal() {
    setShowPhotoRemoveConfirmation(
      prevShowPhotoRemoveConfirmation => !prevShowPhotoRemoveConfirmation
    );
  }

  function handlePhotoRemoval() {
    setShowPhotoRemoveConfirmation(true);
  }

  return (
    <>
      <div role="tree" className="prints-builder__photo_grid">
        {paginatedItems.map(({ id, photo, isLastPhoto }) => {
          const key = `${id}_${photo.size.size_id}`;
          const isSelected = isMultiSelected(photo);

          return (
            <div
              role="treeitem"
              key={key}
              className="photo_grid__item grid-item">
              <div
                className={classNames('photo_grid__container', {
                  selected: isSelected,
                })}
                onClick={() => {
                  showMultiSelectModal && handleMultiSelectItem(photo);
                }}>
                <PreviewBadges
                  photo={photo}
                  handleOnClick={() => {
                    if (!showMultiSelectModal) {
                      handleShowMultiSelectModal();
                      handleMultiSelectItem(photo);
                    }
                  }}
                  isMultiSelectActive={showMultiSelectModal}
                  viewingSizeSelected={!!viewingSizeKey}
                  isSelected={isSelected}>
                  {Photo(
                    {
                      ...photo,
                      onClick: () => {
                        !showMultiSelectModal &&
                          handleSinglePhotoSelection(photo.image_id);
                      },
                    },
                    isLastPhoto,
                    sizesWithRootImage(sizes, id),
                    availableSizes,
                    onAdvancedEditPhoto,
                    recommendedPrintSizes[id],
                    SimpleThumbnail,
                    isSelected,
                    {
                      showLowQualityWarning: true,
                      hideLowQualityInformationTooltip: true,
                    }
                  )}
                </PreviewBadges>
              </div>
            </div>
          );
        })}
        <Footer>
          <Paginator
            title="Uploaded photos pages"
            page={page}
            totalPageCount={totalPageCount}
            scrollTarget={-200}
            onPageNext={handlePageNext}
            onPagePrevious={handlePagePrevious}
            onGoToPage={handleGoToPage}
          />
          <MultiSelectModal
            maximumSelected={maximumSelected}
            multiSelectItemsCount={multiSelectItemsCount}
            handleMultiUnSelectAll={() => {
              handleMultiUnSelectAll();
              handleShowMultiSelectModal();
            }}
            handleMultiSelectAll={handleMultiSelectAll}
            handleShowMultiSelectModal={() => {
              handleMultiUnSelectAll();
              handleShowMultiSelectModal();
            }}
            showMultiSelectModal={
              !showResizeOptionsModal && showMultiSelectModal
            }
            handleMultiDelete={handlePhotoRemoval}
            handleMultiResize={handleResizeOptionsModal}
          />
          <Button
            type="button"
            className="btn btn-outline-secondary mx-auto mt-2"
            onClick={() => {
              hideSummaryDropdown();
              handleAddPhotos();
            }}
            disabled={invalidDeliveryOptionSelected}
            text="Add More Photos"
          />
        </Footer>
      </div>
      <ResizeOptionsModal
        title={'Resize Selected Photos'}
        availableSizes={sizesWithPrice(availableSizes, sizes, deliveryOption)}
        onClick={targetSizeKey => {
          handleMultiResize(targetSizeKey);
          handleResizeOptionsModal();
        }}
        showSizeOptionsModal={showResizeOptionsModal}
        deliveryOption={deliveryOption}
        handleResizeOptionsModal={handleResizeOptionsModal}
        showResizeOptionsModal={showResizeOptionsModal}
      />
      <PhotoRemoveConfirmationModal
        title={photoRemoveConfirmationText}
        className="photo-list-view__photo_removal_confirmation"
        onRemove={() => {
          handleMultiDelete();
          handlePhotoRemoveModal();
        }}
        onClose={handlePhotoRemoveModal}
        isOpen={showPhotoRemoveConfirmation}
      />
    </>
  );
}

PhotoListView.propTypes = {
  deliveryOption: DeliveryOption.isRequired,
  availableSizes: PropTypes.arrayOf(PrintSize).isRequired,
  sizes: PropTypes.arrayOf(PrintSize).isRequired,
  orderedPhotos: PropTypes.array.isRequired,
  handleSinglePhotoSelection: PropTypes.func.isRequired,
  onAdvancedEditPhoto: PropTypes.func.isRequired,
  handleShowMultiSelectModal: PropTypes.func.isRequired,
  showMultiSelectModal: PropTypes.bool.isRequired,
};
